'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function RatioCalculator() {
  const t = useTranslations("tools.ratioCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Ratio proportion state
  const [ratioInputs, setRatioInputs] = useState({
    a: '',
    b: '',
    c: '',
    d: ''
  });
  const [ratioResults, setRatioResults] = useState<any>(null);
  const [ratioError, setRatioError] = useState('');

  // Scale ratio state
  const [scaleInputs, setScaleInputs] = useState({
    a: '',
    b: '',
    factor: '',
    type: 'enlarge'
  });
  const [scaleResults, setScaleResults] = useState<any>(null);
  const [scaleError, setScaleError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const gcd = (a: number, b: number): number => {
    a = Math.round(a * 100);
    b = Math.round(b * 100);
    while (b !== 0) {
      const temp = b;
      b = a % b;
      a = temp;
    }
    return a / 100;
  };

  const simplifyRatio = (a: number, b: number) => {
    const gcdValue = gcd(Math.abs(a), Math.abs(b));
    return {
      a: (a / gcdValue).toFixed(2),
      b: (b / gcdValue).toFixed(2)
    };
  };

  const handleRatioCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setRatioError('');
    setRatioResults(null);

    const a = ratioInputs.a ? parseFloat(ratioInputs.a) : null;
    const b = ratioInputs.b ? parseFloat(ratioInputs.b) : null;
    const c = ratioInputs.c ? parseFloat(ratioInputs.c) : null;
    const d = ratioInputs.d ? parseFloat(ratioInputs.d) : null;

    const filledCount = [a, b, c, d].filter(v => v !== null).length;

    if (filledCount !== 3) {
      setRatioError(t('errorThreeValues'));
      return;
    }

    let result: number;
    let missingVar: string;
    let finalA = a, finalB = b, finalC = c, finalD = d;

    if (a === null) {
      if (b === 0 || d === 0) {
        setRatioError(t('errorDivideZero'));
        return;
      }
      result = (b! * c!) / d!;
      missingVar = 'A';
      finalA = result;
    } else if (b === null) {
      if (a === 0 || c === 0) {
        setRatioError(t('errorDivideZero'));
        return;
      }
      result = (a * d!) / c!;
      missingVar = 'B';
      finalB = result;
    } else if (c === null) {
      if (b === 0 || d === 0) {
        setRatioError(t('errorDivideZero'));
        return;
      }
      result = (a * d!) / b;
      missingVar = 'C';
      finalC = result;
    } else {
      if (a === 0 || c === 0) {
        setRatioError(t('errorDivideZero'));
        return;
      }
      result = (b * c) / a;
      missingVar = 'D';
      finalD = result;
    }

    const ratio1 = (finalA! / finalB!).toFixed(4);
    const ratio2 = (finalC! / finalD!).toFixed(4);
    const simplified1 = simplifyRatio(finalA!, finalB!);
    const simplified2 = simplifyRatio(finalC!, finalD!);

    setRatioResults({
      missingVar,
      result,
      a: finalA,
      b: finalB,
      c: finalC,
      d: finalD,
      ratio1,
      ratio2,
      simplified1,
      simplified2
    });
  };

  const handleScaleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setScaleError('');
    setScaleResults(null);

    const a = scaleInputs.a ? parseFloat(scaleInputs.a) : null;
    const b = scaleInputs.b ? parseFloat(scaleInputs.b) : null;
    const factor = scaleInputs.factor ? parseFloat(scaleInputs.factor) : null;

    if (a === null || b === null || factor === null) {
      setScaleError(t('errorAllValues'));
      return;
    }

    if (factor <= 0) {
      setScaleError(t('errorPositiveFactor'));
      return;
    }

    let newA: number, newB: number;

    if (scaleInputs.type === 'enlarge') {
      newA = a * factor;
      newB = b * factor;
    } else {
      newA = a / factor;
      newB = b / factor;
    }

    const simplified = simplifyRatio(newA, newB);

    setScaleResults({
      originalA: a,
      originalB: b,
      newA,
      newB,
      factor,
      type: scaleInputs.type,
      simplified
    });
  };

  const clearRatioForm = () => {
    setRatioInputs({ a: '', b: '', c: '', d: '' });
    setRatioResults(null);
    setRatioError('');
  };

  const clearScaleForm = () => {
    setScaleInputs({ a: '', b: '', factor: '', type: 'enlarge' });
    setScaleResults(null);
    setScaleError('');
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const calculatorCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginBottom: '1.5rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const cardTitleStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' }
  );

  const ratioFormStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: screenSize === 'mobile' ? '0.5rem' : '0.75rem',
    flexWrap: 'wrap',
    marginBottom: '1.5rem'
  };

  const ratioInputStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      textAlign: 'center' as const,
      transition: 'all 0.3s ease'
    },
    { width: '70px' },
    { width: '90px' },
    { width: '100px' }
  );

  const ratioSeparatorStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const formHintStyle: React.CSSProperties = {
    textAlign: 'center',
    color: colors.textSecondary,
    fontSize: '0.875rem',
    marginBottom: '1rem'
  };

  const buttonGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    flexWrap: 'wrap'
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white'
    },
    { padding: '0.75rem 1.5rem', fontSize: '0.875rem' },
    { padding: '0.875rem 2rem', fontSize: '1rem' },
    { padding: '0.875rem 2rem', fontSize: '1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569'
    },
    { padding: '0.75rem 1.5rem', fontSize: '0.875rem' },
    { padding: '0.875rem 2rem', fontSize: '1rem' },
    { padding: '0.875rem 2rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '1rem',
      marginTop: '2rem',
      border: '2px solid #86efac'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    {
      color: '#166534',
      marginBottom: '1.5rem',
      textAlign: 'center' as const
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
    },
    { padding: '1rem', flexDirection: 'column' as const, gap: '0.5rem', textAlign: 'center' as const },
    { padding: '1.25rem', flexDirection: 'row' as const },
    { padding: '1.25rem', flexDirection: 'row' as const }
  );

  const resultLabelStyle: React.CSSProperties = {
    color: '#475569',
    fontWeight: 500
  };

  const resultValueStyle = getResponsiveStyle(
    {
      color: '#166534',
      fontWeight: 700
    },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const errorMessageStyle: React.CSSProperties = {
    background: '#fef2f2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    border: '1px solid #fecaca',
    textAlign: 'center'
  };

  const visualRepresentationStyle = getResponsiveStyle(
    {
      marginTop: '1.5rem',
      background: 'white',
      borderRadius: '0.75rem'
    },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const visualTitleStyle: React.CSSProperties = {
    color: '#166534',
    fontWeight: 600,
    marginBottom: '1rem',
    textAlign: 'center'
  };

  const ratioBarsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-end',
    justifyContent: 'center',
    height: '150px'
  };

  const ratioBarBaseStyle: React.CSSProperties = {
    flex: 1,
    maxWidth: '100px',
    background: 'linear-gradient(180deg, #2563eb 0%, #7c3aed 100%)',
    borderRadius: '0.5rem 0.5rem 0 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: '0.5rem',
    color: 'white',
    fontWeight: 600,
    position: 'relative' as const
  };

  const barLabelStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: '-1.5rem',
    color: '#475569',
    fontSize: '0.875rem'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem'
    },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem'
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    {
      lineHeight: 1.8,
      color: colors.textSecondary,
      paddingLeft: '1.5rem',
      marginBottom: '1rem'
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const strongStyle: React.CSSProperties = {
    color: colors.textPrimary,
    fontWeight: 600
  };

  const scalingSelectStyle: React.CSSProperties = {
    padding: '0.75rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    background: 'white',
    cursor: 'pointer'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={calculatorCardStyle}>
            <div style={cardTitleStyle}>
              <span>📊</span>
              <span>{t('solveRatioTitle')}</span>
            </div>

            <form style={ratioFormStyle} onSubmit={handleRatioCalculate}>
              <input
                type="number"
                style={ratioInputStyle}
                placeholder="A"
                step="any"
                value={ratioInputs.a}
                onChange={(e) => setRatioInputs({...ratioInputs, a: e.target.value})}
              />
              <span style={ratioSeparatorStyle}>:</span>
              <input
                type="number"
                style={ratioInputStyle}
                placeholder="B"
                step="any"
                value={ratioInputs.b}
                onChange={(e) => setRatioInputs({...ratioInputs, b: e.target.value})}
              />
              <span style={ratioSeparatorStyle}>=</span>
              <input
                type="number"
                style={ratioInputStyle}
                placeholder="C"
                step="any"
                value={ratioInputs.c}
                onChange={(e) => setRatioInputs({...ratioInputs, c: e.target.value})}
              />
              <span style={ratioSeparatorStyle}>:</span>
              <input
                type="number"
                style={ratioInputStyle}
                placeholder="D"
                step="any"
                value={ratioInputs.d}
                onChange={(e) => setRatioInputs({...ratioInputs, d: e.target.value})}
              />
            </form>

            <p style={formHintStyle}>{t('ratioHint')}</p>

            <div style={buttonGroupStyle}>
              <button type="button" style={calculateBtnStyle} onClick={handleRatioCalculate}>
                {t('calculateButton')}
              </button>
              <button type="button" style={clearBtnStyle} onClick={clearRatioForm}>
                {t('clearButton')}
              </button>
            </div>

            {ratioError && (
              <div style={errorMessageStyle}>{ratioError}</div>
            )}

            {ratioResults && (
              <div style={resultsStyle}>
                <h3 style={resultsH3Style}>✅ {t('resultsTitle')}</h3>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('missingValue')} ({ratioResults.missingVar}):</span>
                  <span style={resultValueStyle}>{ratioResults.result.toFixed(4)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('completeRatio')}:</span>
                  <span style={resultValueStyle}>
                    {ratioResults.a.toFixed(2)} : {ratioResults.b.toFixed(2)} = {ratioResults.c.toFixed(2)} : {ratioResults.d.toFixed(2)}
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('simplifiedForm')}:</span>
                  <span style={resultValueStyle}>
                    {ratioResults.simplified1.a} : {ratioResults.simplified1.b} = {ratioResults.simplified2.a} : {ratioResults.simplified2.b}
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('ratioValue')}:</span>
                  <span style={resultValueStyle}>{ratioResults.ratio1} ≈ {ratioResults.ratio2}</span>
                </div>
                <div style={visualRepresentationStyle}>
                  <div style={visualTitleStyle}>{t('visualRepresentation')}</div>
                  <div style={ratioBarsStyle}>
                    <div style={{...ratioBarBaseStyle, height: `${(ratioResults.a / Math.max(ratioResults.a, ratioResults.b, ratioResults.c, ratioResults.d)) * 100}%`}}>
                      {ratioResults.a.toFixed(1)}
                      <span style={barLabelStyle}>A</span>
                    </div>
                    <div style={{...ratioBarBaseStyle, height: `${(ratioResults.b / Math.max(ratioResults.a, ratioResults.b, ratioResults.c, ratioResults.d)) * 100}%`}}>
                      {ratioResults.b.toFixed(1)}
                      <span style={barLabelStyle}>B</span>
                    </div>
                    <div style={{...ratioBarBaseStyle, height: `${(ratioResults.c / Math.max(ratioResults.a, ratioResults.b, ratioResults.c, ratioResults.d)) * 100}%`}}>
                      {ratioResults.c.toFixed(1)}
                      <span style={barLabelStyle}>C</span>
                    </div>
                    <div style={{...ratioBarBaseStyle, height: `${(ratioResults.d / Math.max(ratioResults.a, ratioResults.b, ratioResults.c, ratioResults.d)) * 100}%`}}>
                      {ratioResults.d.toFixed(1)}
                      <span style={barLabelStyle}>D</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={calculatorCardStyle}>
            <div style={cardTitleStyle}>
              <span>📏</span>
              <span>{t('scaleRatioTitle')}</span>
            </div>

            <form style={ratioFormStyle} onSubmit={handleScaleCalculate}>
              <input
                type="number"
                style={ratioInputStyle}
                placeholder="A"
                step="any"
                value={scaleInputs.a}
                onChange={(e) => setScaleInputs({...scaleInputs, a: e.target.value})}
              />
              <span style={ratioSeparatorStyle}>:</span>
              <input
                type="number"
                style={ratioInputStyle}
                placeholder="B"
                step="any"
                value={scaleInputs.b}
                onChange={(e) => setScaleInputs({...scaleInputs, b: e.target.value})}
              />
              <select
                style={scalingSelectStyle}
                value={scaleInputs.type}
                onChange={(e) => setScaleInputs({...scaleInputs, type: e.target.value})}
              >
                <option value="enlarge">{t('enlarge')}</option>
                <option value="shrink">{t('shrink')}</option>
              </select>
              <input
                type="number"
                style={ratioInputStyle}
                placeholder={t('factorPlaceholder')}
                step="any"
                value={scaleInputs.factor}
                onChange={(e) => setScaleInputs({...scaleInputs, factor: e.target.value})}
              />
              <span style={ratioSeparatorStyle}>{t('times')}</span>
            </form>

            <p style={formHintStyle}>{t('scaleHint')}</p>

            <div style={buttonGroupStyle}>
              <button type="button" style={calculateBtnStyle} onClick={handleScaleCalculate}>
                {t('calculateButton')}
              </button>
              <button type="button" style={clearBtnStyle} onClick={clearScaleForm}>
                {t('clearButton')}
              </button>
            </div>

            {scaleError && (
              <div style={errorMessageStyle}>{scaleError}</div>
            )}

            {scaleResults && (
              <div style={resultsStyle}>
                <h3 style={resultsH3Style}>✅ {t('scaledRatioTitle')}</h3>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('originalRatio')}:</span>
                  <span style={resultValueStyle}>{scaleResults.originalA} : {scaleResults.originalB}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('operation')}:</span>
                  <span style={resultValueStyle}>
                    {scaleResults.type === 'enlarge' ? t('enlarged') : t('shrunk')} {t('by')} {scaleResults.factor}x
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('newRatio')}:</span>
                  <span style={resultValueStyle}>{scaleResults.newA.toFixed(4)} : {scaleResults.newB.toFixed(4)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('simplified')}:</span>
                  <span style={resultValueStyle}>{scaleResults.simplified.a} : {scaleResults.simplified.b}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('insight1Title')}</strong> {t('insight1Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('insight2Title')}</strong> {t('insight2Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('insight3Title')}</strong> {t('insight3Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('insight4Title')}</strong> {t('insight4Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('detailsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('practice1Title')}</strong> {t('practice1Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('practice2Title')}</strong> {t('practice2Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('practice3Title')}</strong> {t('practice3Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('practice4Title')}</strong> {t('practice4Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('practice5Title')}</strong> {t('practice5Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://www.myfitnesspal.com" target="_blank" rel="noopener noreferrer" style={{color: colors.primary, textDecoration: 'none'}}>
              MyFitnessPal: Calorie Tracker & BMR Calculator
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://www.calculator.net/macro-calculator.html" target="_blank" rel="noopener noreferrer" style={{color: colors.primary, textDecoration: 'none'}}>
              Macro Calculator - Calculator.net
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://www.inchcalculator.com/health-calculators/" target="_blank" rel="noopener noreferrer" style={{color: colors.primary, textDecoration: 'none'}}>
              Health & Fitness Calculators - Inch Calculator
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://zozofit.com" target="_blank" rel="noopener noreferrer" style={{color: colors.primary, textDecoration: 'none'}}>
              ZOZOFIT: Precise 3D Body Measurement App
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}